import { getEnv, types } from "mobx-state-tree";
import { PricesStorageModel } from "./PricesStorage";
import { api } from "../api/ApiService";
import { activityPricesStorage, ActivityTypes, getActivityLabel } from "./ActivityPricesStorage";
import { ColumnType } from "../../components/datatable/interfaces";
import { GridColDef } from "@mui/x-data-grid";
import CounterCell from "./CounterCell";
import React from "react";
import { translationService } from "../translation/TranslationService";
import { reaction } from "mobx";
import { organizationSubscriptionService } from "../organization/OrganizationSubscription";
import { Flags } from "../../generated/FeatureFlags";
import { featureFlagsStorage } from "../feature-flags/FeatureFlagsStorage";
import { formatEuro } from "./formatEuro";

// The shop sells balance in units of one full SmartApp run (Inspection Data Capture + Damage
// Detection + Damage Highlighting), quoted at a flat price per unit. This is the shop's own
// display price only: what a run is actually charged still comes from activity_prices.
export const ORDER_UNIT_EUROS = 30;
// The same unit in euro-cents, the unit every amount is stored and sent in.
export const ORDER_UNIT_CENTS = ORDER_UNIT_EUROS * 100;

export const getOrderColumns = (translationService: any): GridColDef[] => [
  {
    field: "service",
    headerName: translationService.translate("shop.activity-prices.app"),
    type: ColumnType.String,
    width: 120,
    sortable: false,
    renderCell: (params) => <div className="wrap-cell">{params.value}</div>,
  },
  {
    field: "orderedApps",
    headerName: translationService.translate("shop.activity-prices.ordered"),
    type: ColumnType.Number,
    width: 140,
    sortable: false,
    renderCell: (params) => <CounterCell value={params.row.orderedApps} activity={params.row.id} />,
  },
  {
    field: "totalCostEuro",
    headerName: translationService.translate("shop.activity-prices.total-euro"),
    type: ColumnType.Number,
    flex: 1,
    sortable: false,
    align: "center",
    renderCell: (params) => <div style={{fontWeight: params.row.bold ? "bold" : "normal"}}>{params.row.totalCostEuro}</div>
  },
];

const OrderStorageModel = types
  .model({
    conversionAmount: types.optional(types.number, 0),
    analysisAmount: types.optional(types.number, 0),
    highlightAmount: types.optional(types.number, 0),
    ocrAmount: types.optional(types.number, 0),
    analysisAndSegmentationAmount: types.optional(types.number, 0),
    ocrAndAnalysisAmount: types.optional(types.number, 0),
  })
  .named("OrderStorageModel")
  .actions((self) => ({
    setConversionAmount(amount: number) {
      self.conversionAmount = amount;
    },
    setAnalysisAmount(amount: number) {
      self.analysisAmount = amount;
    },
    setHighlightAmount(amount: number) {
      self.highlightAmount = amount;
    },
    setOcrAmount(amount: number) {
      self.ocrAmount = amount;
    },
    setAnalysisAndSegmentationAmount(amount: number) {
      self.analysisAndSegmentationAmount = amount;
    },
    setOcrAndAnalysisAmount(amount: number) {
      self.ocrAndAnalysisAmount = amount;
    },
    reset() {
      self.conversionAmount = 0;
      self.analysisAmount = 0;
      self.highlightAmount = 0;
      self.ocrAmount = 0;
      self.analysisAndSegmentationAmount = 0;
      self.ocrAndAnalysisAmount = 0;
    },
  }))
  .views((self) => ({
    get columns() {
      const translationService = getEnv(self).translationService;
      return getOrderColumns(translationService);
    },
    get rows() {
      const translationService = getEnv(self).translationService;
      const featureFlagsStorage = getEnv(self).featureFlagsStorage;
      
      const conversionItem = {
        id: ActivityTypes.VideoConversion,
        service: translationService.translate(getActivityLabel([ActivityTypes.VideoConversion])),
        orderedApps: self.conversionAmount,
        totalOrderedCents: this.conversionTotalCents,
        totalCostEuro: formatEuro(this.conversionTotalEuros * 100),
      }
      const highlightItem = {
        id: ActivityTypes.HighlightDetection,
        service: translationService.translate(getActivityLabel([ActivityTypes.HighlightDetection])),
        orderedApps: self.highlightAmount,
        totalOrderedCents: this.highlightTotalCents,
        totalCostEuro: formatEuro(this.highlightTotalEuros * 100),
      }
      const analysisItem = {
        id: ActivityTypes.VideoAnalysis,
        service: translationService.translate(getActivityLabel([ActivityTypes.VideoAnalysis])),
        orderedApps: self.analysisAmount,
        totalOrderedCents: this.analysisTotalCents,
        totalCostEuro: formatEuro(this.analysisTotalEuros * 100),
      }
      const analysisAndSegmentationItem = {
        id: ActivityTypes.VideoAnalysisAndSegmentation,
        service: translationService.translate(getActivityLabel([ActivityTypes.VideoAnalysis, ActivityTypes.HighlightDetection])),
        orderedApps: self.analysisAndSegmentationAmount,
        totalOrderedCents: this.analysisAndSegmentationTotalCents,
        totalCostEuro: formatEuro(this.analysisAndSegmentationTotalEuros * 100),
      }
      const ocrItem =  {
        id: ActivityTypes.VideoOcr,
        service: translationService.translate(getActivityLabel([ActivityTypes.VideoOcr])),
        orderedApps: self.ocrAmount,
        totalOrderedCents: this.ocrTotalCents,
        totalCostEuro: formatEuro(this.ocrTotalEuros * 100),
      }
      const ocrAndAnalysisItem =  {
        id: ActivityTypes.VideoOcrAndAnalysis,
        service: translationService.translate(getActivityLabel([ActivityTypes.VideoOcr, ActivityTypes.VideoAnalysis])),
        orderedApps: self.ocrAndAnalysisAmount,
        totalOrderedCents: this.ocrAndAnalysisTotalCents,
        totalCostEuro: formatEuro(this.ocrAndAnalysisTotalEuros * 100),
      }
      const items =
      [
        {
          id: -3,
          service: translationService.translate("shop.activity-prices.final-row"),
          orderedApps: -1,
          totalOrderedCents: "",
          totalCostEuro: formatEuro(this.finalEuros * 100),
          bold: true
        },
        {
          id: -2,
          service: `${translationService.translate("shop.activity-prices.discount-row")} (${this.discount}%)`,
          orderedApps: -1,
          totalOrderedCents: "",
          totalCostEuro: formatEuro(this.discountEuros * 100),
        },
        {
          id: -1,
          service: translationService.translate("shop.activity-prices.total-row"),
          orderedApps: -1,
          totalOrderedCents: this.totalCents,
          totalCostEuro: formatEuro(this.totalEuros * 100),
        },
      ]
      if (featureFlagsStorage.isFeatureEnabled(Flags.Enable_Video_Conversion)) items.push(conversionItem)
      if (featureFlagsStorage.isFeatureEnabled(Flags.Enable_Video_Segmentation)) items.push(highlightItem)
      if (featureFlagsStorage.isFeatureEnabled(Flags.Enable_Video_Analysis)) items.push(analysisItem)
      if (featureFlagsStorage.isFeatureEnabled(Flags.Enable_Analysis_And_Segmentation)) items.push(analysisAndSegmentationItem)
      if (featureFlagsStorage.isFeatureEnabled(Flags.Enable_Ocr)) items.push(ocrItem)
      if (featureFlagsStorage.isFeatureEnabled(Flags.Enable_Ocr_Analysis)) items.push(ocrAndAnalysisItem)

      return items.reverse();
    },
    get unitCents() {
      return ORDER_UNIT_CENTS;
    },
    get conversionTotalCents() {
      return self.conversionAmount * this.unitCents;
    },
    get conversionTotalEuros() {
      return self.conversionAmount * ORDER_UNIT_EUROS;
    },
    get analysisTotalCents() {
      return self.analysisAmount * this.unitCents;
    },
    get analysisTotalEuros() {
      return self.analysisAmount * ORDER_UNIT_EUROS;
    },
    get highlightTotalCents() {
      return self.highlightAmount * this.unitCents;
    },
    get highlightTotalEuros() {
      return self.highlightAmount * ORDER_UNIT_EUROS;
    },
    get ocrTotalCents() {
      return self.ocrAmount * this.unitCents;
    },
    get ocrTotalEuros() {
      return self.ocrAmount * ORDER_UNIT_EUROS;
    },
    get analysisAndSegmentationTotalCents() {
      return self.analysisAndSegmentationAmount * this.unitCents;
    },
    get analysisAndSegmentationTotalEuros() {
      return self.analysisAndSegmentationAmount * ORDER_UNIT_EUROS;
    },
    get ocrAndAnalysisTotalCents() {
      return self.ocrAndAnalysisAmount * this.unitCents;
    },
    get ocrAndAnalysisTotalEuros() {
      return self.ocrAndAnalysisAmount * ORDER_UNIT_EUROS;
    },
    get totalCents() {
      return this.conversionTotalCents + this.analysisTotalCents + this.highlightTotalCents + this.ocrTotalCents + this.analysisAndSegmentationTotalCents + this.ocrAndAnalysisTotalCents;
    },
    get totalEuros() {
      return this.conversionTotalEuros + this.analysisTotalEuros + this.highlightTotalEuros + this.ocrTotalEuros + this.analysisAndSegmentationTotalEuros + this.ocrAndAnalysisTotalEuros;
    },
    get discount() {
      return getEnv(self).pricesStorage.getDiscount(this.totalCents);
    },
    get discountEuros() {
      return (this.totalEuros * this.discount) / 100;
    },
    get finalEuros() {
      return this.totalEuros - this.discountEuros;
    },
  }));

const pricesStorage = PricesStorageModel.create({ noPagination: true }, { api });

export const orderStorage = OrderStorageModel.create(
  { conversionAmount: 0, analysisAmount: 0, highlightAmount: 0 },
  {
    activityPricesStorage: activityPricesStorage,
    pricesStorage: pricesStorage,
    translationService,
    featureFlagsStorage
  }
);

reaction( 
  () => organizationSubscriptionService.key,
  async () => {
    await pricesStorage.load();
})
